<script setup>
// Assistente de IA — moldura do módulo: título, abas e os avisos que valem para tudo.
//
// O estado do módulo (kill switch e criptografia) é buscado UMA vez aqui, e não em cada tela:
// são avisos sobre o servidor, não sobre a página.
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import assistenteIaApi from 'dashboard/api/assistenteIa';
import Banner from 'dashboard/components-next/banner/Banner.vue';

const { t } = useI18n();
const route = useRoute();
const { accountScopedRoute } = useAccount();

const estado = ref(null);
// Com 500 ou tempo esgotado, os avisos de "desligado" e de "sem criptografia" sumiam calados —
// e a tela parecia normal quando não era (AIA-07, C5).
const estadoFalhou = ref(false);

const abas = [
  { chave: 'ASSISTENTES', rota: 'assistente_ia_home' },
  { chave: 'CREDENCIAIS', rota: 'assistente_ia_credenciais' },
  { chave: 'CAIXAS', rota: 'assistente_ia_caixas' },
  { chave: 'ATENDIMENTOS', rota: 'assistente_ia_atendimentos' },
  { chave: 'CONSUMO', rota: 'assistente_ia_consumo' },
];

// As telas de um assistente (editar, base, playground) continuam com a aba Assistentes ativa.
const rotasDeAssistente = [
  'assistente_ia_home',
  'assistente_ia_assistente',
  'assistente_ia_base',
  'assistente_ia_playground',
];

const abaAtiva = aba =>
  aba.rota === 'assistente_ia_home'
    ? rotasDeAssistente.includes(route.name)
    : route.name === aba.rota;

onMounted(async () => {
  try {
    const { data } = await assistenteIaApi.estado();
    estado.value = data.payload;
    estadoFalhou.value = false;
  } catch (e) {
    estado.value = null;
    estadoFalhou.value = true;
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full gap-4 p-6 overflow-auto font-inter">
    <div class="flex flex-col gap-1">
      <h1 class="text-heading-1 text-n-slate-12">
        {{ t('ASSISTENTE_IA.TITULO') }}
      </h1>
      <p class="mb-0 max-w-3xl text-body-main text-n-slate-11">
        {{ t('ASSISTENTE_IA.DESCRICAO') }}
      </p>
    </div>

    <Banner v-if="estadoFalhou" color="amber">
      {{ t('ASSISTENTE_IA.AVISOS.ESTADO_DESCONHECIDO') }}
    </Banner>

    <Banner v-if="estado && !estado.habilitado" color="amber">
      {{ t('ASSISTENTE_IA.AVISOS.DESLIGADO') }}
    </Banner>

    <Banner v-if="estado && !estado.criptografia_configurada" color="ruby">
      {{ t('ASSISTENTE_IA.AVISOS.SEM_CRIPTOGRAFIA') }}
    </Banner>

    <nav class="flex flex-wrap items-center gap-1 border-b border-n-weak">
      <router-link
        v-for="aba in abas"
        :key="aba.rota"
        :to="accountScopedRoute(aba.rota)"
        class="px-3 py-2 text-sm font-medium border-b-2 -mb-px"
        :class="
          abaAtiva(aba)
            ? 'border-n-brand text-n-slate-12'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
        "
      >
        {{ t(`ASSISTENTE_IA.ABAS.${aba.chave}`) }}
      </router-link>
    </nav>

    <router-view />
  </div>
</template>
